import { Eye, TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCradSize } from "@/hooks/use-card-size";
import { TruncatedText } from "@/components/common/truncated-text";
import { IconButton } from "@/components/common/icon-button";
import { Image } from "@/components/common/image";
import { DeleteConfirmModal, Tooltip } from "@/components/ui/modal";
import macosFolderBlack from '@/assets/agent-user-detail/macos-folder-black@3x.webp';
import { useEffect, useState } from "react";
import { AddFileIcon, MaterialEmptyImage } from "./assets";
import { KnowledgeModalDetail } from "@/components/knowledge-model-detail";
import { KnowledgeModalAdd } from "@/components/knowledge-model-add";
import { agentDashboardKnowledgeHooks } from "@/manager";
import { KnowledgeEmptyIcon } from "./assets/icon/knowledge-empty-icon";

interface KnowledgeBaseProps {
  activeTab: string;
}

/** 知识库 */
export const KnowledgeBase = ({ activeTab }: KnowledgeBaseProps) => {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectId, setSelectId] = useState(null);
  const [detailFolderName, setDetailFolderName] = useState<string>('');

  // 知识库列表数据
  const { agentUserKnowledge, searchAgentUserKnowledge, loading } = agentDashboardKnowledgeHooks.useAgentUserKnowledgeData();
  // 知识库目录数据 (用于弹窗列表数据)
  const { knowledgeFolders, loading: knowledgeLoading } = agentDashboardKnowledgeHooks.useKnowledgeFoldersData();
  // 新建知识库
  const { agentUserKnowledgeCreate, loading: createLoading } = agentDashboardKnowledgeHooks.useAgentUserKnowledgeCreate()
  // 删除知识库
  const { agentUserKnowledgeDelete, loading: deleteLoading } = agentDashboardKnowledgeHooks.useAgentUserKnowledgeDelete()
  // 知识库详情
  const { knowledgeFolderItems, loading: detailLoading, fetchKnowledgeFolderItems } = agentDashboardKnowledgeHooks.useAgentUserKnowledgeItems()

  const { ref, columns, width, height } = useCradSize({
    min: 170,
    max: 220,
    gap: 3,
  });

  useEffect(() => {
    searchAgentUserKnowledge();
  }, [activeTab])

  const handleCreate = async (folderIds: number[]) => {
    try {
      await agentUserKnowledgeCreate(folderIds);
    } catch (error) {
      console.log(error);
    }
  }

  const handleDelete = (id: number) => {
    setDeleteModalOpen(true);
    setSelectId(id);
  }

  const confirmDelete = async () => {
    await agentUserKnowledgeDelete(Number(selectId));
    setDeleteModalOpen(false);
  }

  const handleDetail = async (folderId: number, folderName: string) => {
    fetchKnowledgeFolderItems(folderId);
    setDetailModalOpen(true);
    setDetailFolderName(folderName);
  }

  return (
    <div className="w-full h-full flex flex-col relative mb-20">
      <div className='flex items-center justify-between text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
        <span>{t("agent_dashboard.user_detail.knowledge.title")}</span>
        <KnowledgeModalAdd
          trigger={
            <button className='h-10 flex items-center justify-center py-[10px] px-[14px] border border-[rgba(213,215,218,1)] hover:bg-gray-50  rounded-md shadow-sm transition-all'>
              <AddFileIcon />
              <span className='ml-1.5 text-sm text-[rgba(16,24,40,1)] font-semibold'>{t("agent_dashboard.user_detail.knowledge.add")}</span>
            </button>
          }
          loading={createLoading}
          knowledgeFolders={knowledgeFolders}
          onSave={handleCreate}
        />
      </div>
      <div
        ref={ref}
        className='grid gap-x-2 gap-y-18'
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {loading && <KnowledgeBaseSkeleton />}
        {!loading && agentUserKnowledge.length ? agentUserKnowledge.map((item, index) => (
          <div key={item.id} className="w-full h-full relative">
            <div className="group relative w-full rounded-[12px] mb-[10px] transition-all cursor-pointer select-none">
              <div className='overflow-hidden flex-shrink-0'
                onClick={() => handleDetail(item.folderId, item.folderName)}
              >
                <Image src={macosFolderBlack} className="size-full object-cover" />
              </div>
              <div className='opacity-0 group-hover:opacity-100 absolute bottom-[14px] right-[14px] transition-all flex gap-1 bg-[rgba(0,0,0,0.4)] rounded-[8px] px-1 py-1'>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Eye className="w-4 h-4 text-white" />}
                      onClick={() => handleDetail(item.folderId, item.folderName)}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.knowledge.view_detail")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<TrashIcon className="w-4 h-4 text-white" />}
                      onClick={() => handleDelete(item.folderId)}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.knowledge.delete")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              </div>
            </div>
            <div className="absolute bottom-1 left-[10px] w-full translate-y-full">
              <div className="w-full text-center">
                <TruncatedText text={item.folderName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
              </div>
              <div className="w-full text-center mt-1">
                <TruncatedText text={`${item.itemCount} ${t("agent_dashboard.user_detail.knowledge.add_modal.items")}`} className='max-w-[90%] text-sm text-[rgba(102,112,133,1)]' />
              </div>
            </div>
          </div>
        )) : !loading && (
          <div className=' w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16'>
            <KnowledgeEmptyIcon className='text-[rgba(102,112,133,1)]' />
            <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.knowledge.knowledge_empty")}</span>
          </div>
        )}
      </div>
      {deleteModalOpen && (
        <DeleteConfirmModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          onConfirm={confirmDelete}
          loading={deleteLoading}
        />
      )}
      {detailModalOpen && (
        <KnowledgeModalDetail
          open={detailModalOpen}
          loading={detailLoading}
          name={detailFolderName}
          knowledgeFolderItems={knowledgeFolderItems}
          onOpenChange={setDetailModalOpen}
        />
      )}
    </div>
  )
}

// 骨架屏组件
function KnowledgeBaseSkeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="w-full h-full relative animate-pulse">
          <div className="w-full rounded-[12px] mb-[10px] bg-gray-200 aspect-square"></div>
          <div className="absolute bottom-1 w-full translate-y-full">
            <div className="w-full text-center">
              <div className="max-w-[70%] mx-auto h-5 bg-gray-200 rounded"></div>
            </div>
            <div className="w-full text-center mt-1">
              <div className="max-w-[20%] mx-auto h-3 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}